// src/components/auth/PasswordExpiryModal.jsx
// Modal that appears when user's password is expiring (3-day warning) or expired (90+ days)
// Shows at each login to create urgency for password change

import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Lock, Clock } from 'lucide-react';

export default function PasswordExpiryModal({
  isOpen,
  onClose,
  isExpired,
  daysRemaining
}) {
  const navigate = useNavigate();

  const handleChangePassword = () => {
    // Navigate to Profile page and open password change dialog
    navigate('/Profile', { state: { openPasswordDialog: true } });
    if (!isExpired) {
      onClose();
    }
  };

  const handleDismiss = () => {
    // Only allow dismissal for warning state, not expired
    if (!isExpired) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={isExpired ? undefined : onClose}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton={isExpired}
        onPointerDownOutside={isExpired ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={isExpired ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4">
            {isExpired ? (
              <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-red-600" />
              </div>
            ) : (
              <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-8 w-8 text-amber-600" />
              </div>
            )}
          </div>
          <DialogTitle className="text-xl">
            {isExpired
              ? 'Twoje hasło wygasło'
              : 'Twoje hasło wkrótce wygaśnie'
            }
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {isExpired ? (
              <>
                Ze względów bezpieczeństwa Twoje hasło wygasło po 90 dniach.
                <br />
                <span className="font-medium text-red-600">
                  Musisz ustawić nowe hasło, aby kontynuować.
                </span>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-amber-700">
                    Pozostało {daysRemaining} {daysRemaining === 1 ? 'dzień' : 'dni'}
                  </span>
                </div>
                Twoje hasło wygaśnie za {daysRemaining} {daysRemaining === 1 ? 'dzień' : 'dni'}.
                <br />
                Zalecamy zmianę hasła teraz, aby uniknąć przerw w dostępie.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Security info */}
        <div className={`rounded-lg p-3 text-sm ${
          isExpired
            ? 'bg-red-50 border border-red-200 text-red-700'
            : 'bg-amber-50 border border-amber-200 text-amber-700'
        }`}>
          <p className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {isExpired
                ? 'Polityka bezpieczeństwa KConnect wymaga zmiany hasła co 90 dni dla ochrony Twojego konta.'
                : 'Regularna zmiana hasła chroni Twoje konto przed nieautoryzowanym dostępem.'
              }
            </span>
          </p>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={handleChangePassword}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            <Lock className="h-4 w-4 mr-2" />
            Zmień hasło teraz
          </Button>

          {!isExpired && (
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="w-full text-slate-500"
            >
              Przypomnij później
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
